/**
 * Ce qu'un client possède : ses sites, les comptes qui y sont rattachés, et les connexions
 * dont ces comptes proviennent.
 *
 * ## Pourquoi un module, et pas une fonction par écran
 *
 * Cinq écrans lisent le client choisi dans la barre du haut — le Gestionnaire et les quatre
 * entrées de son fil d'Ariane (Connecteurs, Comptes, Sites, Clients). Ils n'ont pas les mêmes
 * données sous la main, mais ils posent **la même question**, et la recopier cinq fois est la
 * garantie de la voir diverger : un écran montrerait un compte sous un client, le voisin non,
 * et rien ne casserait.
 *
 * ⚠️ **Le rattachement s'écrit de plusieurs façons**, et c'est tout le piège. `Site.accountIds`
 * mêle l'identifiant de base (un cuid), l'identifiant de régie — avec ou sans tirets —, le
 * numéro nu d'un site de mesure, et la forme qualifiée `{fournisseur}-{connexion}-{numéro}`.
 * Comparer des identifiants nus lit une partie du parc comme non rattachée, **en silence** :
 * l'absence de rattachement étant le cas majoritaire, rien ne distingue l'un de l'autre.
 */

import { fabriquerResolveurDeRattachement } from './sitesDeMesure';
import { identitesDuCompte, ensembleAutorise } from './accountMatching';

/**
 * Les résolveurs des deux plateformes qui numérotent leurs sites **par instance**.
 *
 * Ils se bâtissent sur **tous** les comptes de la plateforme : c'est la comparaison entre
 * instances qui dit si un numéro nu est ambigu. Les bâtir instance par instance conclurait
 * « unique » sur chacune d'elles.
 */
export const resolveursDeRattachement = (comptes = []) => ({
  MATOMO: fabriquerResolveurDeRattachement(
    'matomo', comptes.filter(a => a.provider === 'MATOMO'), ['sourceId', 'idSite']
  ),
  PIANO_ANALYTICS: fabriquerResolveurDeRattachement(
    'piano', comptes.filter(a => a.provider === 'PIANO_ANALYTICS'), ['sourceId', 'siteId']
  )
});

/**
 * « Ce rattachement enregistré désigne-t-il ce compte ? », pour un compte donné.
 *
 * Matomo et Piano passent par leur résolveur, qui refuse un numéro nu ambigu au lieu de retenir
 * le premier site venu. Les autres plateformes comparent leurs identités.
 *
 * ⚠️ **Les tirets se normalisent DES DEUX CÔTÉS.** `identitesDuCompte` rend déjà les deux
 * écritures de l'identifiant de régie ; sans `ensembleAutorise` sur la valeur stockée, un compte
 * rattaché sous « 123-456-7890 » et portant « 1234567890 » n'est reconnu que dans un sens —
 * alors que c'est le « ou l'inverse » que `accountMatching.js` promet.
 */
export const predicatDeRattachement = (compte, resolveurs) => {
  const resolveur = resolveurs?.[compte?.provider];
  if (resolveur) return (valeur) => resolveur.correspond(compte, valeur);

  const identites = identitesDuCompte(compte);
  return (valeur) => {
    const ecritures = ensembleAutorise([valeur]);
    return identites.some(identite => ecritures.has(identite));
  };
};

/**
 * La clé sous laquelle un compte est dédoublonné à l'affichage — donc le nombre de **cartes**
 * ou de lignes qu'une liste ouvrira. Les décomptes en dérivent, sans quoi une pastille
 * annoncerait plus de comptes que la liste n'en montre.
 */
export const cleDeCarte = (compte) => `${compte.provider}-${compte.sourceId || compte.id}`;

/**
 * La clé d'une **ligne de bibliothèque**, et c'est elle qui décide de l'appartenance.
 *
 * ⚠️ **Ne pas confondre les deux.** `cleDeCarte` fond délibérément les doublons de Matomo et de
 * Piano, dont le `sourceId` n'est qu'un numéro de site : « 2 » désigne « Espace Locataires »
 * chez une instance et « Paris en Scène » chez une autre. S'en servir pour dire si un compte
 * appartient au client laisserait entrer le jumeau de l'autre instance — exactement le
 * rattachement croisé que `fabriquerResolveurDeRattachement` existe pour refuser.
 */
export const cleDeLigne = (compte) => `${compte.provider}-${compte.databaseId || compte.libraryId || compte.id}`;

/**
 * Les comptes rattachés à l'un de ces sites.
 *
 * ⚠️ **On ne lit jamais un champ `siteId` déjà posé sur le compte** : les écrans qui en
 * calculent un n'en retiennent qu'**un**, le premier site trouvé. Or un compte peut être
 * rattaché aux sites de deux clients d'une même agence : il disparaîtrait de l'un des deux
 * périmètres, sans un mot.
 */
export const comptesDesSites = (comptes = [], sitesVises = [], resolveurs = null) => {
  const table = resolveurs || resolveursDeRattachement(comptes);
  return comptes.filter(compte => {
    const correspond = predicatDeRattachement(compte, table);
    return sitesVises.some(site => (site.accountIds || []).some(correspond));
  });
};

/**
 * Les connecteurs dont ces comptes proviennent.
 *
 * ⚠️ **Un connecteur est une CONNEXION, pas une plateforme.** Deux comptes Google peuvent venir
 * de deux branchements distincts : retenir le fournisseur rendrait les connecteurs d'un
 * confrère sous le nom de ce client.
 *
 * ⚠️ **Un compte sans `connectionId` ne ferme rien** : les lignes de bibliothèque antérieures à
 * cette colonne n'en portent pas, et les écarter masquerait un connecteur réellement employé.
 * Le fournisseur sert alors de repli, qui élargit plutôt qu'il n'affirme.
 */
export const connecteursDesComptes = (connecteurs = [], comptes = []) => {
  const connexions = new Set(comptes.map(a => a.connectionId).filter(Boolean));
  const fournisseursSansConnexion = new Set(
    comptes.filter(a => !a.connectionId).map(a => a.provider)
  );
  return new Set(
    connecteurs
      .filter(c => connexions.has(c.id) || fournisseursSansConnexion.has(c.provider))
      .map(c => c.id)
  );
};

/**
 * Le périmètre complet d'un client, prêt à borner une liste.
 *
 * ⚠️ **Trois états, pas deux.** `null` veut dire « aucun client filtré », et ne retire rien —
 * c'est l'état d'origine, et toute liste reste entière. Un client filtré rend des ensembles,
 * **même vides** : un client sans site ne voit aucun compte, et c'est une réponse. Un appelant
 * qui ne sait pas encore — une requête en vol, un refus — doit passer `null` lui aussi, jamais
 * un client dont les données manquent : une liste qui s'éteint pendant son chargement affirme
 * une absence qu'aucune réponse n'a établie.
 */
export const perimetreDuClient = ({ clientFiltre, sites = [], comptes = [], connecteurs = [] }) => {
  if (!clientFiltre) return null;

  const sitesDuClient = sites.filter(s => s.clientId === clientFiltre);
  const retenus = comptesDesSites(comptes, sitesDuClient);

  return {
    clientIds: new Set([clientFiltre]),
    siteIds: new Set(sitesDuClient.map(s => s.id)),
    // L'appartenance se juge sur la ligne, le décompte sur la carte : voir `cleDeLigne`.
    lignes: new Set(retenus.map(cleDeLigne)),
    nombreDeCartes: new Set(retenus.map(cleDeCarte)).size,
    connecteurIds: connecteursDesComptes(connecteurs, retenus)
  };
};
